// src/components/CallEvalDisplay.js
import React from 'react';

const isUsableScore = (value) => {
    if (value === null || typeof value === 'undefined') return false;
    const parsed = typeof value === 'string' ? Number(value) : value;
    return Number.isFinite(parsed) && parsed >= 0;
};

// Helper 1: Renders a single score item as a readable pill.
const ScoreItem = ({ name, value }) => {
    if (!isUsableScore(value)) return null;
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-1"
            style={{ backgroundColor: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-text-secondary)' }}
        >
            <span className="capitalize">{name}</span>
            <span className="font-semibold" style={{ color: 'var(--pallium-text-primary)' }}>{value}</span>
        </span>
    );
};

// Helper 2: Renders a category block with scores in a specific order
const ScoreCategoryBlock = ({ title, scores, prefixes, order }) => { // Accept 'order' prop

    // --- CORRECTED LOGIC: Process scores ONCE per category ---
    const categoryScores = Object.entries(scores) // Iterate over all scores ONCE
        .filter(([key, value]) => // Filter scores relevant to THIS category block
            // Check if the key starts with ANY of the prefixes for this group AND ends with _score
            prefixes.some(p => key.startsWith(p + '_')) &&
            key.endsWith('_score') &&
            (typeof value === 'number' || value === null || typeof value === 'string')
        )
        .map(([key, value]) => { // Map the filtered scores
             // --- Name and BaseKey Extraction (Same as before) ---
             let baseKey = key;
             let formattedName = key;

             if (baseKey.endsWith('_score')) {
                 baseKey = baseKey.substring(0, baseKey.length - '_score'.length);
                 formattedName = baseKey;
             }

             if (baseKey.startsWith('scarf_')) {
                 baseKey = baseKey.substring('scarf_'.length);
             } else if (baseKey.startsWith('call_structure_')) {
                 baseKey = baseKey.substring('call_structure_'.length);
             }

             // Formatting for display name
            if (formattedName.startsWith('call_structure_opening')) {
                formattedName = 'Opening';
            } else if (formattedName.startsWith('call_structure_info_gathering')) {
                formattedName = 'Info Gathering';
            } else if (formattedName.startsWith('call_structure_solution_presentation')) {
                formattedName = 'Solution Presentation';
            } else if (formattedName.startsWith('call_structure_closing')) {
                formattedName = 'Closing';
            } else if (formattedName.startsWith('scarf_')) {
                 formattedName = formattedName.substring('scarf_'.length);
            }
             formattedName = formattedName.replace(/_/g, ' ').trim();
             if (formattedName) {
                 formattedName = formattedName.charAt(0).toUpperCase() + formattedName.slice(1);
             }
             // --- End Name/BaseKey Logic ---

            return {
                name: formattedName || key,
                value: isUsableScore(value) ? value : null,
                originalKey: key,
                baseKey: baseKey
            };
        })
        // --- Sorting Logic (Same as before) ---
        .sort((a, b) => {
            if (!order) {
                return a.name.localeCompare(b.name);
            }
            const indexA = order.indexOf(a.baseKey);
            const indexB = order.indexOf(b.baseKey);

            if (indexA === -1 && indexB === -1) {
                return a.name.localeCompare(b.name);
            }
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
        // --- End Sorting Logic ---


    const visibleScores = categoryScores.filter((score) => isUsableScore(score.value));
    if (visibleScores.length === 0) return null; // Don't render block if no usable scores match

    // --- Rendering Logic (Same as before) ---
    return (
        <div className="mt-3">
            <h5 className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--pallium-text-tertiary)' }}>{title}</h5>
            <div className="flex flex-wrap gap-1.5">
                {visibleScores.map(score => (
                    <ScoreItem key={score.originalKey} name={score.name} value={score.value} />
                ))}
            </div>
        </div>
    );
};


// Main Component: Displays Call Evaluations
const CallEvalDisplay = ({ evaluations }) => {
    if (!Array.isArray(evaluations) || evaluations.length === 0) {
        return null;
    }

    const visibleEvaluations = evaluations.filter((ev) => {
        const scores = ev?.scores || {};
        return Object.values(scores).some(isUsableScore);
    });
    const hiddenCount = evaluations.length - visibleEvaluations.length;

    // Define categories, prefixes, and the desired display ORDER
    // (This part remains the same)
    const categoryGroups = [
         {
            title: 'SCARF',
            prefixes: ['scarf'],
            order: ['status', 'certainty', 'autonomy', 'relatedness', 'fairness']
        },
        {
            title: 'Communication Effectiveness',
            prefixes: ['empathy', 'assertiveness', 'respect', 'clarity'],
            order: ['clarity', 'empathy', 'assertiveness', 'respect']
        },
        {
            title: 'Call Structure',
            prefixes: ['call_structure_opening', 'call_structure_info_gathering', 'call_structure_solution_presentation', 'call_structure_closing'],
            order: ['opening', 'info_gathering', 'solution_presentation', 'closing']
        },
    ];

    // --- Rendering Logic (Same as before) ---
    return (
        <div className="text-xs space-y-3 mt-3">
            {hiddenCount > 0 && (
                <div
                    className="rounded-md px-3 py-2"
                    style={{ backgroundColor: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-text-secondary)' }}
                >
                    {hiddenCount} participant{hiddenCount === 1 ? '' : 's'} omitted because there was insufficient scoring data.
                </div>
            )}
            {visibleEvaluations.map((ev, index) => (
                ev && ev.scores ? (
                    <div
                        key={ev.staffId || index}
                        className="rounded-lg p-3"
                        style={{ backgroundColor: 'var(--pallium-white)', border: '1px solid var(--pallium-stroke-light)' }}
                    >
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <p className="text-sm font-semibold" style={{ color: 'var(--pallium-text-primary)' }}>{ev.staffName || 'Unknown Staff'}</p>
                            {isUsableScore(ev.scores.overall_score) && (
                                <span className="rounded-full px-2 py-1 text-xs font-semibold" style={{ backgroundColor: 'var(--pallium-purple-0-5)', color: 'var(--pallium-primary-purple)' }}>
                                    Overall {ev.scores.overall_score}
                                </span>
                            )}
                        </div>
                        <div className="space-y-1.5">
                           {categoryGroups.map(group => (
                               <ScoreCategoryBlock
                                   key={group.title}
                                   title={group.title}
                                   scores={ev.scores}
                                   prefixes={group.prefixes}
                                   order={group.order}
                               />
                           ))}
                        </div>
                          <div className='mt-2 space-y-1'>
                             {ev.scores.key_strengths && <p className="text-xs leading-relaxed"><strong style={{ color: 'var(--pallium-text-secondary)' }}>Strengths:</strong> {ev.scores.key_strengths}</p>}
                             {ev.scores.areas_for_improvement && <p className="text-xs leading-relaxed"><strong style={{ color: 'var(--pallium-text-secondary)' }}>Improvements:</strong> {ev.scores.areas_for_improvement}</p>}
                          </div>
                    </div>
                 ) : null
            ))}
        </div>
    );
};

export default CallEvalDisplay;